/** Presentation only: closing never submits or mutates the expense. */
export function bindExpenseSheet(dialog: HTMLDialogElement, options: { extraState: () => string; discard: () => void }): void {
  const form = dialog.querySelector<HTMLFormElement>("form")!;
  const discardDialog = document.querySelector<HTMLDialogElement>("#expense-discard-dialog")!;
  const cancel = dialog.querySelector<HTMLButtonElement>(".close-dialog")!;
  const fingerprint = () => JSON.stringify([...form.querySelectorAll<HTMLInputElement | HTMLSelectElement>("input,select")].map(input =>
    [input.id, input.value, input instanceof HTMLInputElement ? input.checked : false])) + options.extraState();
  let initial = fingerprint();
  let returnFocus: HTMLElement | null = null;
  const viewport = window.visualViewport;
  const resize = () => {
    if (!dialog.open || !viewport) return;
    dialog.style.setProperty("--sheet-viewport-height", `${viewport.height}px`);
    dialog.style.setProperty("--sheet-keyboard-bottom", `${Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)}px`);
  };
  const observer = new MutationObserver(() => {
    if (!dialog.isConnected) { observer.disconnect(); return; }
    if (dialog.open) {
      initial = fingerprint();
      dialog.querySelector(".expense-sheet-fields")?.scrollTo(0, 0);
      returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      resize();
      viewport?.addEventListener("resize", resize);
      viewport?.addEventListener("scroll", resize);
      // Opening the sheet must not summon the keyboard before the user chooses a field.
      cancel.focus({ preventScroll: true });
    } else {
      viewport?.removeEventListener("resize", resize);
      viewport?.removeEventListener("scroll", resize);
    }
  });
  observer.observe(dialog, { attributes: true, attributeFilter: ["open"] });
  const requestClose = () => {
    if (fingerprint() === initial) { dialog.close(); return; }
    if (!discardDialog.open) discardDialog.showModal();
  };
  cancel.addEventListener("click", requestClose);
  dialog.addEventListener("cancel", event => { event.preventDefault(); requestClose(); });
  dialog.addEventListener("click", event => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) requestClose();
  });
  discardDialog.querySelector("[data-keep-expense]")?.addEventListener("click", () => discardDialog.close());
  discardDialog.querySelector("[data-discard-expense]")?.addEventListener("click", () => {
    discardDialog.close(); options.discard(); dialog.close();
  });
  // A downward gesture is accepted only on the header, never while scrolling fields.
  const header = dialog.querySelector<HTMLElement>(".form-top")!;
  let pointer: { x: number; y: number; id: number } | undefined;
  header.addEventListener("pointerdown", event => {
    if ((event.target as HTMLElement).closest("button") || event.pointerType === "mouse") return;
    pointer = { x: event.clientX, y: event.clientY, id: event.pointerId };
    header.setPointerCapture(event.pointerId);
  });
  header.addEventListener("pointerup", event => {
    if (pointer?.id === event.pointerId && event.clientY - pointer.y > 75 && Math.abs(event.clientX - pointer.x) < 60) requestClose();
    pointer = undefined;
  });
  header.addEventListener("pointercancel", () => { pointer = undefined; });
  dialog.addEventListener("close", () => {
    viewport?.removeEventListener("resize", resize);
    viewport?.removeEventListener("scroll", resize);
    if (returnFocus?.isConnected && !returnFocus.closest("#expense-dialog")) returnFocus.focus({ preventScroll: true });
  });
}
